// Lightweight word-level diff for the note "what changed" view. No
// dependency — an LCS over whitespace-delimited tokens, which reads well
// for prose edits (the clinician sees added/removed words, not characters).
//
// Note bodies can contain HTML; we strip tags first so the diff shows
// textual changes cleanly rather than diffing markup. This is a change
// SUMMARY, not a rich-text editor.

export type DiffSegment = { value: string; type: "same" | "added" | "removed" };

export function stripHtml(text: string): string {
  return (text || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/[ \t]+/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Split into tokens while keeping whitespace runs as their own tokens, so
// rejoining preserves spacing.
function tokenize(text: string): string[] {
  return text.match(/\s+|[^\s]+/g) || [];
}

/**
 * Word-level diff of two strings → ordered segments. Equal runs are merged
 * so the renderer emits compact spans. HTML is stripped before diffing.
 */
export function diffWords(oldText: string, newText: string): DiffSegment[] {
  const a = tokenize(stripHtml(oldText));
  const b = tokenize(stripHtml(newText));

  // LCS table over tokens (classic dynamic-programming edit alignment).
  const m = a.length;
  const n = b.length;
  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const raw: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      raw.push({ value: a[i], type: "same" });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      raw.push({ value: a[i], type: "removed" });
      i++;
    } else {
      raw.push({ value: b[j], type: "added" });
      j++;
    }
  }
  while (i < m) raw.push({ value: a[i++], type: "removed" });
  while (j < n) raw.push({ value: b[j++], type: "added" });

  // Merge adjacent same-type segments into single spans.
  const merged: DiffSegment[] = [];
  for (const seg of raw) {
    const last = merged[merged.length - 1];
    if (last && last.type === seg.type) last.value += seg.value;
    else merged.push({ ...seg });
  }
  return merged;
}

/** True when two note-section strings differ in their visible text. */
export function textChanged(oldText: string, newText: string): boolean {
  return stripHtml(oldText) !== stripHtml(newText);
}
